import 'date-fns';
import React, {useRef, useState} from 'react';
import {Box, Grid, MenuItem, Select, Typography, makeStyles, FormHelperText} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import InputText from '../../../UI/EmcApplicationQuestions/inputs/core/InputText';
import {z} from 'zod';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useCommonModalStyles} from '@/Agent/Quotes/InstantAppModalPages/common/modalStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FixedNavigation from '@/Agent/Quotes/InstantAppModalPages/common/FixedNavigation';
import StickyTopNavigation from '@/Agent/Quotes/InstantAppModalPages/common/StickyTopNavigation';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {Autocomplete, useJsApiLoader} from '@react-google-maps/api';
import {STATE_OPTIONS} from '@/Agent/constants';

interface AddressProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onNext: () => void;
    onBack?: () => void;
}

const useStyles = makeStyles(() => ({
    container: {
        maxWidth: 700,
        width: '100%',
        position: 'relative',
        paddingBottom: 113,
        margin: '0 auto',
    },
    title: {
        marginBottom: 40,
        marginTop: 40,
        fontFamily: 'LoraSemiBold',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 40,
        lineHeight: '110%',
        color: '#3F51B5',
        opacity: 0.9,
    },
    form: {
        display: 'flex',
        rowGap: 4,
    },
}));

const formSchema = z.object({
    address: z.string().min(1, 'Street Address is required'),
    unit: z.string().optional(),
    addressCity: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    addressZipCode: z
        .string()
        .min(1, {message: 'Zip code is required'})
        .refine((value) => /^\d{5}(-\d{4})?$/.test(value.replace(/\s/g, '')), {
            message: 'Zip must be in a valid format',
        }),
});

type FormSchema = z.infer<typeof formSchema>;

type Address = {
    Street: string;
    City: string;
    State: string;
    ZipCode: string;
};

const LIBRARIES: ['places'] = ['places'];

const Address: React.FC<AddressProps> = observer(({onNext, onBack, onExit, parameterStore, contactFormStore}) => {
    const classes = useStyles();
    const commonClasses = useCommonModalStyles();
    const {approval, onValueChangedPreApproval} = parameterStore;

    const {trigger, control, handleSubmit, setValue, formState} = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            address: approval.address.value || '',
            unit: '',
            addressCity: approval.addressCity.value || '',
            state: approval.state.value || '',
            addressZipCode: approval.addressZipCode.value || '',
        },
    });

    const {isLoaded, loadError} = useJsApiLoader({
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY || '',
        libraries: LIBRARIES,
        language: 'en-US',
        region: 'us',
    });

    const [autocomplete, setAutocomplete] = useState<any | null>(null);
    const [addressAutofill, setAddressAutofill] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const handleLoad = (autocompleteInstance: any) => {
        setAutocomplete(autocompleteInstance);
    };

    function onAddressSelect(address: Address) {
        setValue('address', address?.Street);
        setValue('addressCity', address?.City);
        setValue('state', address?.State);
        setValue('addressZipCode', address?.ZipCode);
        if (!addressAutofill) {
            setAddressAutofill(true);
        }
    }

    React.useEffect(() => {
        if (approval.addressCity.value || approval.addressCity.value || approval.addressCity.value) {
            setAddressAutofill(true);
        }
    }, []);

    const onPlacesChanged = () => {
        const place = autocomplete?.getPlace();
        const myAddress: Address = {
            Street: '',
            City: '',
            State: '',
            ZipCode: '',
        };

        let streetNumber,
            streetName = '';

        place?.address_components?.forEach(function (c: {types: any[]; short_name: string; long_name: string}) {
            switch (c.types[0]) {
                case 'street_number':
                    streetNumber = c.short_name;
                    break;
                case 'route':
                    streetName = c.long_name;
                    break;
                case 'neighborhood':
                case 'locality': // North Hollywood or Los Angeles?
                    myAddress.City = c.long_name;
                    break;
                case 'administrative_area_level_1': //  Note some countries don't have states
                    myAddress.State = c.short_name;
                    break;
                case 'postal_code':
                    myAddress.ZipCode = c.short_name;
                    break;
            }
        });

        myAddress.Street = `${streetNumber || ''} ${streetName || ''}`.trim();

        onAddressSelect(myAddress);
    };

    const onSubmit = (data: FormSchema) => {
        onValueChangedPreApproval(
            {...approval.address, name: 'address'},
            `${data.address}${data.unit ? '  Unit ' + data.unit : ''}`,
        );
        onValueChangedPreApproval({...approval.addressCity, name: 'addressCity'}, data.addressCity);
        onValueChangedPreApproval({...approval.addressZipCode, name: 'addressZipCode'}, data.addressZipCode);
        onValueChangedPreApproval({...approval.state, name: 'state'}, data.state);
        contactFormStore.saveForm();
        onNext();
    };

    return (
        <Box className={classes.container}>
            <StickyTopNavigation onExit={onExit} />
            <Typography className={classes.title}>Please begin typing your address...</Typography>
            <Grid container spacing={2} component="form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
                <Grid item xs={addressAutofill || loadError ? 8 : 12} ref={containerRef}>
                    <Typography className={commonClasses.label}>Street Address</Typography>
                    <Controller
                        name="address"
                        control={control}
                        render={({field, fieldState: {error}}) => (
                            <>
                                {isLoaded && (
                                    <Autocomplete
                                        onLoad={handleLoad}
                                        onUnmount={() => setAutocomplete(null)}
                                        options={{
                                            componentRestrictions: {country: 'us'},
                                        }}
                                        onPlaceChanged={onPlacesChanged}
                                        types={['address']}
                                    >
                                        <InputText
                                            {...field}
                                            aria-label="Street Address"
                                            placeholder="Type Your Address"
                                            error={!!error}
                                            helperText={error?.message}
                                            classes={{root: commonClasses.input}}
                                        />
                                    </Autocomplete>
                                )}
                                {loadError && (
                                    <InputText
                                        {...field}
                                        aria-label="Street Address"
                                        placeholder="Type Your Address"
                                        error={!!error}
                                        helperText={error?.message}
                                        classes={{root: commonClasses.input}}
                                    />
                                )}
                            </>
                        )}
                    />
                </Grid>
                {(addressAutofill || loadError) && (
                    <>
                        <Grid item xs={4}>
                            <Typography className={commonClasses.label}>Unit (Optional)</Typography>
                            <Controller
                                name="unit"
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <InputText
                                        {...field}
                                        aria-label="Unit (Optional)"
                                        placeholder="i.e. “#100” or “C”"
                                        error={!!error}
                                        helperText={error?.message}
                                        classes={{root: commonClasses.input}}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={commonClasses.label}>City</Typography>
                            <Controller
                                name="addressCity"
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <InputText
                                        {...field}
                                        aria-label="City"
                                        placeholder="City"
                                        error={!!error}
                                        helperText={error?.message}
                                        classes={{root: commonClasses.input}}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={commonClasses.label}>State</Typography>
                            <Controller
                                name="state"
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <>
                                        <Select
                                            {...field}
                                            fullWidth
                                            aria-label="State"
                                            variant="outlined"
                                            displayEmpty
                                            error={!!error}
                                            className={commonClasses.select}
                                            IconComponent={ExpandMoreIcon}
                                        >
                                            {STATE_OPTIONS.map((value) => (
                                                <MenuItem key={value} value={value}>
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {!!error && (
                                            <FormHelperText error={!!error} style={{margin: '3px 14px'}}>
                                                {error?.message}
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={commonClasses.label}>Zip Code</Typography>
                            <Controller
                                name="addressZipCode"
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <InputText
                                        {...field}
                                        aria-label="Zip Code"
                                        placeholder="Zip Code"
                                        mask={{mask: [{mask: '00000'}, {mask: '00000 - 0000'}]}}
                                        error={!!error}
                                        onChange={(e) => setValue('addressZipCode', e.target.value)}
                                        onBlur={() => trigger('addressZipCode')}
                                        helperText={error?.message}
                                        classes={{root: commonClasses.input}}
                                    />
                                )}
                            />
                        </Grid>
                    </>
                )}
                <FixedNavigation
                    onBack={onBack}
                    isNextDisabled={!formState.isValid}
                    isLoading={formState.isSubmitting}
                />
            </Grid>
        </Box>
    );
});

export default Address;
